import { useState, useCallback } from 'react';
import type { Job, JobPostingFormData } from '../types';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';

interface UseJobPostingReturn {
  loading: boolean;
  error: string | null;
  createJob: (jobData: JobPostingFormData) => Promise<{ success: boolean; job?: Job; error?: string }>;
  updateJob: (jobId: string, jobData: Partial<JobPostingFormData>) => Promise<{ success: boolean; job?: Job; error?: string }>;
  deleteJob: (jobId: string) => Promise<{ success: boolean; error?: string }>;
  validateJobData: (jobData: JobPostingFormData) => { isValid: boolean; errors: Record<string, string> };
  estimateJobCost: (jobData: JobPostingFormData) => number;
}

export const useJobPosting = (userId: string): UseJobPostingReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invokeFunction } = useAuthenticatedApi();

  const validateJobData = useCallback((jobData: JobPostingFormData): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};

    // Required field validation
    if (!jobData.title.trim()) {
      errors.title = 'Job title is required';
    } else if (jobData.title.length < 10) {
      errors.title = 'Job title must be at least 10 characters';
    }

    if (!jobData.description.trim()) {
      errors.description = 'Job description is required';
    } else if (jobData.description.length < 100) {
      errors.description = 'Job description must be at least 100 characters';
    }

    if (!jobData.category_id) {
      errors.category_id = 'Please select a category';
    }

    if (jobData.required_skills.length === 0) {
      errors.required_skills = 'At least one skill is required';
    }

    // Budget validation
    if (jobData.budget_min && jobData.budget_max && jobData.budget_min > jobData.budget_max) {
      errors.budget_max = 'Maximum budget must be greater than minimum budget';
    }

    if (jobData.budget_min && jobData.budget_min <= 0) {
      errors.budget_min = 'Budget must be greater than 0';
    }

    if (jobData.budget_max && jobData.budget_max <= 0) {
      errors.budget_max = 'Budget must be greater than 0';
    }

    // Timeline validation
    if (jobData.deadline) {
      const deadlineDate = new Date(jobData.deadline);
      const now = new Date();
      if (deadlineDate <= now) {
        errors.deadline = 'Deadline must be in the future';
      }
    }

    if (jobData.expires_at) {
      const expiryDate = new Date(jobData.expires_at);
      const now = new Date();
      if (expiryDate <= now) {
        errors.expires_at = 'Job expiry must be in the future';
      }
    }

    // BroScore requirement validation
    if (jobData.min_bro_score < 0) {
      errors.min_bro_score = 'Minimum BroScore cannot be negative';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, []);

  const estimateJobCost = useCallback((jobData: JobPostingFormData): number => {
    let baseCost = 5; // Base posting cost

    // Featured job costs extra
    if (jobData.featured) {
      baseCost += 15;
    }

    // High priority costs extra
    if (jobData.priority === 'urgent') {
      baseCost += 10;
    } else if (jobData.priority === 'high') {
      baseCost += 5;
    }

    // Longer expiry costs extra
    if (jobData.expires_at) {
      const now = new Date();
      const expiry = new Date(jobData.expires_at);
      const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry > 30) {
        baseCost += 5;
      } else if (daysUntilExpiry > 14) {
        baseCost += 2;
      }
    }

    return baseCost;
  }, []);

  const createJob = useCallback(async (
    jobData: JobPostingFormData
  ): Promise<{ success: boolean; job?: Job; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      console.log('🚀 [JOB_POSTING] Starting job creation:', { jobData, userId });

      // Validate job data
      const validation = validateJobData(jobData);
      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        console.error('❌ [JOB_POSTING] Validation failed:', validation.errors);
        throw new Error(firstError);
      }

      console.log('✅ [JOB_POSTING] Validation passed, calling API...');

      // Call the real job-board-api
      const response = await invokeFunction('job-board-api', {
        action: 'create_job',
        data: jobData
      });

      console.log('📡 [JOB_POSTING] API Response received:', {
        success: response.success,
        error: response.error,
        message: response.message,
        data: response.data,
        fullResponse: response
      });

      if (!response.success) {
        console.error('❌ [JOB_POSTING] API returned validation error:', { 
          success: response.success, 
          error: response.error, 
          message: response.message,
          missingRequirements: response.data?.missing_requirements,
          fullResponse: response
        });
        
        // Show the actual API error message instead of "non-2xx error"
        const errorMessage = response.message || response.error || 'Failed to create job';
        throw new Error(errorMessage);
      }

      console.log('🎉 [JOB_POSTING] Job created successfully:', response.data);
      return { success: true, job: response.data };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create job';
      console.error('💥 [JOB_POSTING] Create job failed:', err);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [userId, validateJobData, invokeFunction]);

  const updateJob = useCallback(async (
    jobId: string,
    jobData: Partial<JobPostingFormData>
  ): Promise<{ success: boolean; job?: Job; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      // Call the real job-board-api for update
      const response = await invokeFunction('job-board-api', {
        action: 'update_job',
        job_id: jobId,
        data: jobData
      });

      if (!response.success) {
        throw new Error(response.message || response.error || 'Failed to update job');
      }

      return { success: true, job: response.data };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update job';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [invokeFunction]);

  const deleteJob = useCallback(async (
    jobId: string
  ): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      // Call the real job-board-api for deletion
      const response = await invokeFunction('job-board-api', {
        action: 'delete_job',
        job_id: jobId
      });

      if (!response.success) {
        throw new Error(response.message || response.error || 'Failed to delete job');
      }

      return { success: true };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete job';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [invokeFunction]);

  return {
    loading,
    error,
    createJob,
    updateJob,
    deleteJob,
    validateJobData,
    estimateJobCost
  };
};